import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Spin,
  Tag,
  Space,
  Tooltip,
  Button,
  Tabs,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import apiClient from '../../services/apiClient';
import {
  formatRatingTypeLabel,
  getRatingRowKey,
  groupRatingsByType,
  sortRatingTypes,
} from '../../utils/creditRatingUtils';
import './CreditRatingsTable.css';

const { Title, Text } = Typography;

const CreditRatingsTable = ({ isin, showActions = false, onEdit, onDelete }) => {
  const [creditRatings, setCreditRatings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeRatingType, setActiveRatingType] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api/v1/sb';

  const ratingsByType = useMemo(() => groupRatingsByType(creditRatings), [creditRatings]);
  const ratingTypes = useMemo(() => sortRatingTypes(Object.keys(ratingsByType)), [ratingsByType]);

  useEffect(() => {
    if (isin && isin.trim() !== '') {
      fetchCreditRatings();
    }
  }, [isin]);

  useEffect(() => {
    if (ratingTypes.length === 0) {
      setActiveRatingType(null);
      return;
    }

    if (!activeRatingType || !ratingTypes.includes(activeRatingType)) {
      setActiveRatingType(ratingTypes[0]);
    }
  }, [ratingTypes, activeRatingType]);

  const fetchCreditRatings = async () => {
    if (!isin || isin.trim() === '') {
      setError('Invalid ISIN provided');
      setCreditRatings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${baseUrl}/master-isin/${isin}/ratings`;
      const response = await apiClient.get(apiUrl);

      if ((response.data.status === 200 || response.data.statusCode === 200) && response.data.data) {
        setCreditRatings(response.data.data);
      } else {
        setCreditRatings([]);
      }
    } catch (err) {
      console.error('Error fetching credit ratings:', err);
      setError(err.message);
      setCreditRatings([]);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'default';

    const ratingValue = rating.toUpperCase();

    if (ratingValue.includes('AAA') || ratingValue.includes('A1+')) return 'green';
    if (ratingValue.includes('AA') || ratingValue.includes('A1')) return 'blue';
    if (ratingValue.includes('A') || ratingValue.includes('A2')) return 'cyan';
    if (ratingValue.includes('BBB') || ratingValue.includes('A3')) return 'orange';
    if (ratingValue.includes('BB') || ratingValue.includes('B1')) return 'volcano';
    if (ratingValue.includes('B') || ratingValue.includes('B2')) return 'red';
    if (ratingValue.includes('CCC') || ratingValue.includes('C')) return 'magenta';

    return 'default';
  };

  const getVerificationStatusIcon = (status) => {
    if (!status) return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;

    const statusValue = status.toLowerCase();

    if (statusValue.includes('verified') || statusValue.includes('confirmed')) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (statusValue.includes('pending') || statusValue.includes('processing')) {
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
    if (statusValue.includes('failed') || statusValue.includes('rejected')) {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }

    return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
  };

  const columns = [
    {
      title: 'Credit Rating Agency Name',
      dataIndex: 'creditRatingAgencyName',
      key: 'creditRatingAgencyName',
      width: 200,
      render: (agency) => <Text strong>{agency || 'N/A'}</Text>,
    },
    {
      title: 'Credit Rating',
      dataIndex: 'currentRating',
      key: 'currentRating',
      width: 120,
      render: (rating) => (
        <Tag color={getRatingColor(rating)} style={{ fontWeight: 'bold' }}>
          {rating || 'N/A'}
        </Tag>
      ),
      align: 'center',
    },
    {
      title: 'Rating Action',
      dataIndex: 'ratingAction',
      key: 'ratingAction',
      width: 120,
      render: (action) => <Text>{action || 'N/A'}</Text>,
      align: 'center',
    },
    {
      title: 'Initial Credit Rating Date',
      dataIndex: 'creditRatingDate',
      key: 'creditRatingDate',
      width: 150,
      render: (date) => <Text>{date || 'N/A'}</Text>,
      align: 'center',
    },
    {
      title: 'Date of Rating Change',
      dataIndex: 'ratingChangeDate',
      key: 'ratingChangeDate',
      width: 150,
      render: (date) => <Text>{date || 'N/A'}</Text>,
      align: 'center',
    },
    {
      title: 'Verification Status',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      width: 140,
      render: (status) => (
        <Space>
          {getVerificationStatusIcon(status)}
          <Text>{status || 'N/A'}</Text>
        </Space>
      ),
      align: 'center',
    },
    {
      title: 'Date of Verification',
      dataIndex: 'dateOfVerification',
      key: 'dateOfVerification',
      width: 150,
      render: (date) => <Text>{date || 'N/A'}</Text>,
      align: 'center',
    },
  ];

  if (showActions) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit && onEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => onDelete && onDelete(record)}
              danger
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    });
  }

  const renderRatingsTable = (ratings) => (
    <Table
      columns={columns}
      dataSource={ratings}
      rowKey={(record, index) => getRatingRowKey(record, index)}
      pagination={false}
      scroll={{ x: 1000 }}
      size="small"
      className="credit-ratings-table"
    />
  );

  const ratingTabs = ratingTypes.map((ratingType) => ({
    key: ratingType,
    label: (
      <span className="credit-ratings-tab-label">
        {formatRatingTypeLabel(ratingType)}
        <span className="credit-ratings-tab-count">({ratingsByType[ratingType].length})</span>
      </span>
    ),
    children: renderRatingsTable(ratingsByType[ratingType]),
  }));

  if (loading) {
    return (
      <div className="credit-ratings-section">
        <div className="ratings-header">
          <Title level={3} className="section-title">
            Credit Ratings
          </Title>
        </div>
        <Card className="ratings-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spin size="large" />
            <div style={{ marginTop: '1rem' }}>Loading credit ratings...</div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="credit-ratings-section">
        <div className="ratings-header">
          <Title level={6} className="section-title">
            Credit Ratings
          </Title>
        </div>
        <Card className="ratings-card">
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0.5rem' }}>
              Error Loading Credit Ratings
            </div>
            <div style={{ fontSize: '14px' }}>{error}</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="credit-ratings-section">
      <div className="ratings-header">
        <Title level={3} className="section-title">
          Credit Ratings
          {creditRatings.length > 0 && (
            <Text type="secondary" style={{ marginLeft: '8px', fontSize: '14px' }}>
              ({creditRatings.length} rating{creditRatings.length !== 1 ? 's' : ''})
            </Text>
          )}
        </Title>
      </div>

      <Card className="ratings-card">
        {creditRatings.length > 0 ? (
          ratingTypes.length > 1 ? (
            <Tabs
              activeKey={activeRatingType}
              onChange={setActiveRatingType}
              items={ratingTabs}
              className="credit-ratings-type-tabs"
              size="small"
            />
          ) : (
            renderRatingsTable(ratingsByType[ratingTypes[0]])
          )
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0.5rem' }}>
              No Credit Ratings Found
            </div>
            <div style={{ fontSize: '14px' }}>
              No credit ratings are available for this ISIN.
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CreditRatingsTable;
